import React, { useState, useEffect } from "react";
import { Tabs, Button, message } from "antd";
import dayjs from 'dayjs';
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router-dom";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import WordPressOrders from "./wordPressOrders";
import TigerTechOrders from "./tigerTechOrders";
import SocrateOrders from "./socrateOrders";
import TransactionsList from "./transactionsList";
import TransactionDetails from "./transactionDetails";
import StripeTransactionsList from "./stripeTransactionsList";
import StripeTransactionDetails from "./stripeTransactionDetails";
import NMITransactionsList from "./NMITransactionsList";
import SublyticsOrders from "./sublyticsOrders";
import SalesReport from "./salesReport";
import SalesUpdate from "./salesUpdate";
import { useTimezone } from "../../core/contexts/TimezoneContext";
import DataFilters from "./components/DataFilters";

const Data = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [transactionTabs, setTransactionTabs] = useState([]);
  const [stripeTransactionTabs, setStripeTransactionTabs] = useState([]);
  const [nmiTransactionTabs, setNmiTransactionTabs] = useState([]);
  const { timezone, setTimezone } = useTimezone();
  
  // Filter states with localStorage persistence
  const [filters, setFilters] = useState(() => {
    // Get saved filters from localStorage or use defaults
    const savedFilters = localStorage.getItem('dataFilters');
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      // Convert dateRange strings back to dayjs objects if they exist
      if (parsedFilters.dateRange && parsedFilters.dateRange.length === 2) {
        parsedFilters.dateRange = [
          dayjs(parsedFilters.dateRange[0]),
          dayjs(parsedFilters.dateRange[1])
        ];
      }
      return parsedFilters;
    }
    return {
      brands: [],
      products: [],
      status: [],
      dateRange: null
    };
  });
  
  const [filtersMinimized, setFiltersMinimized] = useState(() => {
    // Get the saved state from localStorage or default to false if not found
    const savedState = localStorage.getItem('dataFiltersMinimized');
    return savedState ? JSON.parse(savedState) : false;
  });
  
  // Effect to sync localStorage with state changes
  useEffect(() => {
    localStorage.setItem('dataFiltersMinimized', JSON.stringify(filtersMinimized));
  }, [filtersMinimized]);
  
  // Effect to save filters to localStorage when they change
  useEffect(() => {
    // Create a copy of filters that can be safely stringified
    const filtersToSave = { ...filters };
    
    // Convert dayjs objects to ISO strings for storage
    if (filtersToSave.dateRange && filtersToSave.dateRange.length === 2) {
      filtersToSave.dateRange = [
        filtersToSave.dateRange[0].format(),
        filtersToSave.dateRange[1].format()
      ];
    }
    
    localStorage.setItem('dataFilters', JSON.stringify(filtersToSave));
  }, [filters]);
  
  // Apply filters and refresh data
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const handleApplyFilters = () => {
    // Increment refresh trigger to cause child components to refresh
    setRefreshTrigger(prev => prev + 1);
    message.success('Filters applied');
    
    // Save current filters to localStorage (redundant with the useEffect, but ensures immediate save)
    const filtersToSave = { ...filters };
    if (filtersToSave.dateRange && filtersToSave.dateRange.length === 2) {
      filtersToSave.dateRange = [
        filtersToSave.dateRange[0].format(),
        filtersToSave.dateRange[1].format()
      ];
    }
    localStorage.setItem('dataFilters', JSON.stringify(filtersToSave));
  };
  
  // Determine active key based on current URL path
  const getActiveKeyFromPath = () => {
    const path = location.pathname;
    if (path.includes('/data/sales-update')) return 'sales-update';
    if (path.includes('/data/sales-report')) return 'sales-report';
    if (path.includes('/data/sublytics-orders')) return 'sublytics-orders';
    if (path.includes('/data/wordpress-orders')) return 'wordpress-orders';
    if (path.includes('/data/tigertech-orders')) return 'tigertech-orders';
    if (path.includes('/data/socrate-orders')) return 'socrate-orders';
    if (path.includes('/data/authorize-transactions')) return 'authorize-transactions';
    if (path.includes('/data/stripe-transactions')) return 'stripe-transactions';
    if (path.includes('/data/nmi-transactions')) return 'nmi-transactions';
    if (path.startsWith('/data/transaction-')) return path.replace('/data/', '');
    if (path.startsWith('/data/stripe-transaction-')) return path.replace('/data/', '');
    if (path.startsWith('/data/nmi-transaction-')) return path.replace('/data/', '');
    return 'sales-update'; // Default tab
  };
  
  const [activeKey, setActiveKey] = useState(getActiveKeyFromPath());
  
  // Update active key when URL changes
  useEffect(() => {
    setActiveKey(getActiveKeyFromPath());
  }, [location.pathname]);
  
  const onChange = (key) => {
    navigate(`/data/${key}`);
    setActiveKey(key);
  };
  
  const onEdit = (targetKey, action) => {
    if (action === "remove") {
      // Check if it's a regular transaction tab
      if (targetKey.startsWith('transaction-')) {
        setTransactionTabs((prev) => prev.filter((tab) => tab.key !== targetKey));
        if (activeKey === targetKey) {
          setActiveKey("authorize-transactions");
          navigate("/data/authorize-transactions");
        }
      }
      // Check if it's a stripe transaction tab
      else if (targetKey.startsWith('stripe-transaction-')) {
        setStripeTransactionTabs((prev) => prev.filter((tab) => tab.key !== targetKey));
        if (activeKey === targetKey) {
          setActiveKey("stripe-transactions");
          navigate("/data/stripe-transactions");
        }
      }
      // Check if it's an NMI transaction tab
      else if (targetKey.startsWith('nmi-transaction-')) {
        setNmiTransactionTabs((prev) => prev.filter((tab) => tab.key !== targetKey));
        if (activeKey === targetKey) {
          setActiveKey("nmi-transactions");
          navigate("/data/nmi-transactions");
        }
      }
    }
  };
  
  const addTransactionDetailsTab = (transaction) => {
    const tabKey = `transaction-${transaction.transId}`;
    const exists = transactionTabs.some((tab) => tab.key === tabKey);
    
    if (!exists) {
      setTransactionTabs((prev) => [
        ...prev,
        {
          key: tabKey,
          label: `Transaction ${transaction.transId}`,
          transaction: transaction
        }
      ]);
    }
    
    setActiveKey(tabKey);
    navigate(`/data/${tabKey}`);
  };
  
  const addStripeTransactionDetailsTab = (transaction) => {
    const tabKey = `stripe-transaction-${transaction.id}`;
    const exists = stripeTransactionTabs.some((tab) => tab.key === tabKey);
    
    if (!exists) {
      setStripeTransactionTabs((prev) => [
        ...prev,
        {
          key: tabKey,
          label: `Stripe Transaction ${transaction.id}`,
          transaction: transaction
        }
      ]);
    }
    
    setActiveKey(tabKey);
    navigate(`/data/${tabKey}`);
  };

  // Render the appropriate component based on the active tab
  const renderTabContent = () => {
    switch (activeKey) {
      case "sales-update":
        return <SalesUpdate timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "sales-report":
        return <SalesReport timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "sublytics-orders":
        return <SublyticsOrders timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "wordpress-orders":
        return <WordPressOrders timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "tigertech-orders":
        return <TigerTechOrders timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "socrate-orders":
        return <SocrateOrders timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "authorize-transactions":
        return <TransactionsList onViewDetails={addTransactionDetailsTab} timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "stripe-transactions":
        return <StripeTransactionsList onViewDetails={addStripeTransactionDetailsTab} timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      case "nmi-transactions":
        return <NMITransactionsList timezone={timezone} filters={filters} refreshTrigger={refreshTrigger} />;
      default:
        // Check if it's a transaction details tab
        if (activeKey.startsWith("transaction-")) {
          const transaction = transactionTabs.find(tab => tab.key === activeKey)?.transaction;
          return transaction ? <TransactionDetails transaction={transaction} timezone={timezone} /> : <Navigate to="/data/authorize-transactions" />;
        } else if (activeKey.startsWith("stripe-transaction-")) {
          const transaction = stripeTransactionTabs.find(tab => tab.key === activeKey)?.transaction;
          return transaction ? <StripeTransactionDetails transaction={transaction} timezone={timezone} /> : <Navigate to="/data/stripe-transactions" />;
        }
        return null;
    }
  };

  return (
    <div className="page-wrapper" style={{ paddingLeft: "10px" }}>
      <div className="">
        {/* Fixed Filter Sidebar */}
        <div className={`dashboard-sidebar ${filtersMinimized ? 'minimized' : ''}`} style={{ 
          transition: 'width 0.3s ease-in-out',
          width: filtersMinimized ? '50px' : '280px',
          overflow: 'hidden'
        }}>
          <div className="" style={{ position: 'relative' }}>
            <Button 
              type="text"
              icon={filtersMinimized ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => {
                const newState = !filtersMinimized;
                setFiltersMinimized(newState);
                localStorage.setItem('dataFiltersMinimized', JSON.stringify(newState));
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 10
              }}
              size="small"
              title={filtersMinimized ? "Expand filters" : "Collapse filters"}
            />
            <div style={{ opacity: filtersMinimized ? 0 : 1, transition: 'opacity 0.3s ease-in-out' }}>
              <h4 className="px-3 pt-3">Data Filters</h4>
              <DataFilters
                filters={filters}
                setFilters={setFilters}
                onApplyFilters={handleApplyFilters}
                timezone={timezone}
                setTimezone={setTimezone}
              />
            </div>
          </div>
        </div>
        
        {/* Main Content with Tabs - Full width when filters are hidden */}
        <div className={`${filtersMinimized ? 'dashboard-main-expanded' : 'dashboard-main'} px-2`} style={{
          transition: 'margin-left 0.3s ease-in-out',
          marginLeft: filtersMinimized ? '50px' : '280px'
        }}>
          <div className="card">
            <div className="card-body">
          <div style={{ display: "block", alignItems: "center", marginBottom: "10px", width: "100%" }}>
            <Tabs
              activeKey={activeKey}
              onChange={onChange}
              onEdit={onEdit}
              type="editable-card"
              size="small"
              hideAdd={true}
              tabBarStyle={{ marginBottom: "5px" }}
              items={[
                {
                  key: "sales-update",
                  label: "Sales Update",
                  closable: false
                },
                {
                  key: "sales-report",
                  label: "Sales Report",
                  closable: false
                },
                {
                  key: "sublytics-orders",
                  label: "Sublytics Orders",
                  closable: false
                },
                {
                  key: "wordpress-orders",
                  label: "Orders from WordPress",
                  closable: false
                },
                {
                  key: "tigertech-orders",
                  label: "TigerTech Orders",
                  closable: false
                },
                {
                  key: "socrate-orders",
                  label: "Socrate Orders",
                  closable: false
                },
                {
                  key: "authorize-transactions",
                  label: "Authorize.net Transactions",
                  closable: false
                },
                {
                  key: "stripe-transactions",
                  label: "Stripe Transactions",
                  closable: false
                },
                {
                  key: "nmi-transactions",
                  label: "NMI Transactions",
                  closable: false
                },
                ...transactionTabs.map(tab => ({
                  key: tab.key,
                  label: tab.label,
                  closable: true
                })),
                ...stripeTransactionTabs.map(tab => ({
                  key: tab.key,
                  label: tab.label,
                  closable: true
                })),
                ...nmiTransactionTabs.map(tab => ({
                  key: tab.key,
                  label: tab.label,
                  closable: true
                }))
              ]}
            />
          </div>
          {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Data;
